<template>
  <!-- Modal Component -->
  <Modal
    :visible="visible"
    @close="revertSelection"
    ref="modal"
    title="Edit broadband packages"
    style="text-align: left;"
    test-id="broadband-package-modal"
  >
    <template #body>
      <div class="product-options-grid">
        <div
          v-for="option in sortedProducts"
          :key="option.code"
          class="product-option"
          :class="{ checked: tempSelectedProductCode === option.code }"
          @click="selectOption(option)"
          :data-testid="'product-option-' + option.code"
        >
          <label
            :for="option.code"
            class="product__information"
            :class="{
              'show-price': true,
              checked: tempSelectedProductCode === option.code,
            }"
          >
            <img
              class="product-option__img"
              v-if="productImage(option)"
              :class="{
                '--is-selected': tempSelectedProductCode === option.code,
              }"
              :src="productImage(option)"
              :alt="option.title"
            />
            <div class="image-spacer" v-else />

            <div class="product-content">
              <span
                class="product-option__brand"
                v-if="getProductName(option.title)"
              >
                {{ getProductName(option.title) }}
              </span>
              <div class="title-info">
                <h2
                  class="product-option__title"
                  :class="{ 'has-ribbon': !!option.ribbon }"
                >
                  {{ formattedProductBrandName(option.title) }}
                </h2>
                <InfoBubble
                  :text="productInfoLabel(option)"
                  iconColor="#3B4143"
                />
              </div>

              <div class="product-option__body" v-html="option.body" />

              <div class="product-option__price">
                <span
                  class="product-option__price-old"
                  v-if="hasNonDiscountedPrice(option)"
                >
                  £{{ formatNonDiscountedPrice(option) }}
                </span>
                <span
                  v-if="isDefaultBroadbandOption(option)"
                  class="product-option__price-current product-option__price-included"
                  data-testid="broadband-included-in-package"
                >
                  Included in package
                </span>
                <template v-else>
                  <span class="product-option__price-current">
                    +£{{ formatPrice(option) }}
                  </span>
                  <small class="pppw">per person per week</small>
                </template>
              </div>
            </div>
            <span
              class="product-option__ribbon"
              :data-testid="'ribbon_' + option.code"
              v-if="option.ribbon"
              :style="{ backgroundColor: option.ribbon.colour }"
            >
              {{ capitalizeFirstChar(option.ribbon.text) }}
            </span>
          </label>
        </div>
      </div>

      <!-- Checkbox option appears AFTER the grid - single checkbox -->
      <template v-if="allowNoBroadband && noBroadbandOption">
        <div class="checkbox-option">
          <label class="checkbox-label">
            <input
              type="checkbox"
              class="form-checkbox no-broadband-checkbox"
              :class="{
                checked: tempSelectedProductCode === noBroadbandOption.code,
              }"
              data-testid="no-broadband"
              :checked="tempSelectedProductCode === noBroadbandOption.code"
              @change="toggleNoneOption(noBroadbandOption)"
            />
            <span
              >I don't need broadband
              <InfoBubble
                class="info-toggle-checkbox info-desktop-hide"
                :text="productInfoLabel(noBroadbandOption)"
                iconColor="#3B4143"
              />
            </span>
            <InfoBubble
              class="info-toggle-checkbox info-mobile-hide"
              :text="productInfoLabel(noBroadbandOption)"
              iconColor="#3B4143"
            />
          </label>
        </div>
      </template>
    </template>

    <!--    Footer -->
    <template #footer>
      <div class="modal-footer-content">
        <button
          @click="revertSelection"
          class="back-btn-modal btn-mobile-hide"
          data-testid="back-btn-modal"
        >
          Back
        </button>
        <p class="update-total" data-testid="update-total">
          Total now: £{{ formattedPreviewTotal }} per person per week
        </p>
        <div class="bottom-row">
          <button
            @click="$emit('close')"
            class="back-btn-modal btn-desktop-hide"
          >
            Back
          </button>
          <button
            @click="updateSelection"
            class="btn-update-broadband"
            data-testid="update-selection"
            :disabled="!tempSelectedProductCode || selectionError"
          >
            Update selection
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from ".././Modal.vue";
import InfoBubble from "@/components/InfoBubble.vue";

export default {
  name: "BroadbandSelectorModal",
  components: {
    InfoBubble,
    Modal,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      default: () => [],
    },
    selected: String,
  },
  data() {
    return {
      selectedProductCode: this.selected || null,
      tempSelectedProductCode: this.selected,
      previousSelectedProductCode: this.selected || null,
      infoPanelShowing: null,
      agreedToPolicy: false,
      selectionError: false,
      errorResponse: null,
    };
  },
  computed: {
    discountApplied: {
      get() {
        return this.$store.state.discount.isValid;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValid: value });
      },
    },
    tempOption() {
      return (
        this.products.find((p) => p.code === this.tempSelectedProductCode) ||
        null
      );
    },
    committedOption() {
      return (
        this.products.find((p) => p.code === this.selectedProductCode) || null
      );
    },
    previewPricePerPersonInPence() {
      const storePrice = this.$store.getters.pricePerPerson || 0;
      const committedBroadband =
        (this.committedOption &&
          this.committedOption.price &&
          this.committedOption.price.current) ||
        0;
      const tempBroadband =
        (this.tempOption &&
          this.tempOption.price &&
          this.tempOption.price.current) ||
        0;
      return storePrice + (tempBroadband - committedBroadband);
    },
    previewNonDiscountedPricePerPersonInPence() {
      const storePrice = this.$store.getters.nonDiscountedPricePerPerson || 0;

      const committedBroadband = this.committedOption
        ? this.committedOption.price.old
          ? this.committedOption.price.old
          : this.committedOption.price.current
        : 0;

      const tempBroadband = this.tempOption
        ? this.tempOption.price.old
          ? this.tempOption.price.old
          : this.tempOption.price.current
        : 0;

      return storePrice + (tempBroadband - committedBroadband);
    },
    formattedPreviewTotal() {
      const pence = this.discountApplied
        ? this.previewPricePerPersonInPence
        : this.previewNonDiscountedPricePerPersonInPence;
      const value = Math.max(0, (pence || 0) / 100);
      return value.toFixed(2);
    },
    defaultBroadbandProductOption() {
      return this.$store.getters.defaultBroadbandProductOption;
    },
    nonDiscountedPricePerPersonPerWeek() {
      return (this.$store.getters.nonDiscountedPricePerPerson / 100).toFixed(2);
    },
    pricePerPersonPerWeek() {
      return (this.$store.getters.pricePerPerson / 100).toFixed(2);
    },
    sortedProducts() {
      return this.products
        .filter((p) => !p.code.includes("_NONE"))
        .sort((a, b) => this.extractSpeed(b.code) - this.extractSpeed(a.code));
    },
    // UHO: show no-broadband only when flag is true
    allowNoBroadband() {
      return (
        this.$store.state.featureFlags.hasOwnProperty("allow_no_broadband") &&
        this.$store.state.featureFlags["allow_no_broadband"].value === true
      );
    },
    noBroadbandOption() {
      return this.products.find((p) => p.code.includes("_NONE")) || null;
    },
  },
  methods: {
    productImage(productOption) {
      if (this.getProductName(productOption.title) === "Virgin Media") {
        return "/icon-virgin-media.svg";
      } else if (this.getProductName(productOption.title) === "BT Broadband") {
        return "https://www.splitthebills.co.uk/wp-content/uploads//2024/06/BT-Logo.png";
      } else {
        return "/icon-broadband.svg";
      }
    },
    extractSpeed(code) {
      const match = code.match(/(\d+)(?!.*\d)/);
      return match ? parseInt(match[1], 10) : 0;
    },
    formatNonDiscountedPrice(option) {
      if (!option.price.old || !this.defaultBroadbandProductOption) {
        return null;
      }

      const delta =
        (option.price.old - this.defaultBroadbandProductOption.price.current) /
        100;
      return Math.abs(delta).toFixed(2);
    },
    formatPrice(option) {
      if (!option.price.current || !this.defaultBroadbandProductOption) {
        return "0.00";
      }

      const delta =
        (option.price.current -
          this.defaultBroadbandProductOption.price.current) /
        100;
      return Math.abs(delta).toFixed(2);
    },
    hasNonDiscountedPrice(option) {
      return !!option.price.old;
    },
    isDefaultBroadbandOption(option) {
      return (
        this.defaultBroadbandProductOption &&
        option.code === this.defaultBroadbandProductOption.code
      );
    },
    selectOption(option) {
      this.errorResponse = null;
      this.selectionError = false;

      this.tempSelectedProductCode = option.code;
    },
    toggleNoneOption(option) {
      if (!option) return;

      if (this.tempSelectedProductCode === option.code) {
        this.tempSelectedProductCode = null;
      } else {
        this.tempSelectedProductCode = option.code;
      }
    },
    getProductName(title) {
      if (!title) return "";
      if (title.includes("Virgin")) return "Virgin Media";
      if (title.includes("Fibre")) return "BT Broadband";
      return "";
    },
    formattedProductBrandName(title) {
      if (!title) return "";
      let newTitle = title;
      if (title.includes("Virgin"))
        newTitle = newTitle.replace("Virgin Media", "").trim();
      if (title.includes("Fibre")) newTitle = newTitle.replace("BT", "").trim();
      return newTitle;
    },
    capitalizeFirstChar(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },
    productInfoLabel(productOption) {
      switch (productOption.code.toLowerCase()) {
        case "virgin-fibre-1000":
          return (
            "Want the fastest internet possible? Get online with Virgin Media's brand new Voom 1000, and benefit from superfast optical fibre internet! " +
            "Download speeds up to 1000 Mbps. Upload speeds up to 35 Mbps. 100% unlimited downloads. Free installation. No line rental needed. "
          );
        case "virgin-fibre-600":
          return "Download speeds up to 600 Mbps. Upload speeds up to 35 Mbps. 100% unlimited downloads. Free installation. No line rental needed. ";
        case "virgin-fibre-400":
          return "Download speeds up to 350 Mbps. Upload speeds up to 10 Mbps. 100% unlimited downloads. Free installation. No line rental needed.";
        case "plusnet-fibre":
          return "Average download speeds of 66 Mbps. Average upload speed of 18 Mbps. Unlimited downloads. Line rental included. Installation fee included in package price. ";
        case "bt-full-fibre-900":
          return "Download speeds of up to 900Mbps. Upload speed of up to 110Mbps. Unlimited downloads. Line rental included.";
        case "no-broadband-uh-vm-01_none":
          return "Your broadband might be included in your rent or you could've sorted this already. That's not a problem. If anything changes, you can always add broadband to your package at a later date.";
        case "no-broadband-uh-01_none":
          return "Your broadband might be included in your rent or you could've sorted this already. That's not a problem. If anything changes, you can always add broadband to your package at a later date.";
      }
    },
    updateSelection() {
      if (!this.tempSelectedProductCode) {
        this.selectionError = true;
        this.errorResponse =
          "No selection is made, please select one broadband product";
        return;
      }

      this.selectionError = false;
      this.errorResponse = null;

      // Commit the selection
      this.previousSelectedProductCode = this.tempSelectedProductCode;
      this.selectedProductCode = this.tempSelectedProductCode;

      const option = this.products.find(
        (op) => op.code === this.selectedProductCode,
      );

      if (option) {
        this.$emit("option-selected", option.code);
        this.$emit("price-changed", option.price.current);
      } else {
        this.$emit("option-selected", null);
        this.$emit("price-changed", 0);
      }

      this.$emit("close");
    },
    revertSelection() {
      this.tempSelectedProductCode = this.previousSelectedProductCode;
      this.updateSelection();
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.tempSelectedProductCode = this.selectedProductCode;
      }
    },
    selected(newVal) {
      this.selectedProductCode = newVal || null;
      this.tempSelectedProductCode = newVal || null;
    },
    products() {
      if (
        this.selectedProductCode &&
        !this.products.find((p) => p.code === this.selectedProductCode)
      ) {
        this.selectedProductCode = null;
      }

      if (
        this.tempSelectedProductCode &&
        !this.products.find((p) => p.code === this.tempSelectedProductCode)
      ) {
        this.tempSelectedProductCode = this.selectedProductCode;
      }
    },
  },
  created() {
    this.selectedProductCode = this.selected || null;
    this.tempSelectedProductCode = this.selected || null;
  },
  emits: ["close", "option-selected", "price-changed"],
};
</script>

<style lang="scss" scoped>
.product-option-header {
  display: flex;
  height: 24px;
  margin-bottom: 0;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  color: #3b4143;
  font-size: 14px;
  text-align: left;
  font-weight: 700;
  line-height: 21px;

  @include media("<large") {
    display: none;
  }
}
.product-options-grid {
  display: grid;
  row-gap: 16px;
  column-gap: 16px;
  align-self: stretch;
  grid-template-rows: fit-content(100%) fit-content(100%);
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 0;

  @include media("<large") {
    display: flex;
    flex-direction: column;
    height: auto;
  }
}

.product-option {
  position: relative;

  @include media(">=medium") {
    flex-basis: 100%;
  }
}

input {
  @include visuallyhidden;
}

.product__information {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background-color: $white;
  border: 1px solid $grey-mid;
  border-radius: $border-radius-large;
  padding: 16px 8px;
  display: grid;
  grid-template-rows: auto min-content;
  grid-template-columns: 60px 1fr auto;
  grid-auto-flow: column;

  &.show-price {
    grid-template-rows: min-content auto min-content;
  }

  &.checked {
    border-color: $brand-blue;
    box-shadow: 0 0 0 2px $brand-blue;
    background: $white;
  }
}

.product-option__img {
  width: 40px;
  height: 40px;
  min-width: 30px;
  fill: currentColor;
  opacity: 0.7;
  filter: grayscale(100%);
  transition: opacity 0.2s linear, filter 0.4s linear;
  margin-bottom: $spacing-xs;
  grid-row: 1 / 2;
  justify-self: center;

  .show-price > & {
    grid-row: 1 / 3;
    margin-bottom: 0;
  }

  @include media("<medium") {
    max-width: 40px;
  }

  path {
    fill: currentColor;
  }

  &.--is-selected {
    fill: inherit;
    opacity: 1;
    filter: grayscale(0%);

    path {
      fill: inherit;
    }
  }
}

.product-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  grid-column: 2;
  align-items: flex-start;
  gap: 4px;

  @include media("<large") {
    justify-content: flex-start;
  }
}

.title-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.product-option__info-btn {
  text-align: center;
  width: 17px;
  height: 17px;
  font-size: 1.2rem;
  font-weight: 500;
  color: $white;
  background-color: #afafaf;
  border: none;
  border-radius: 50%;
  padding: 0;
  margin: 0;
  justify-self: center;
  align-self: center;
  grid-row-start: 2;

  .show-price > & {
    grid-row-start: 3;
  }
}

.product-option__brand {
  color: #3b4143;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  text-align: left;
}

.product-option__title {
  text-align: initial;
  margin: 0;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
  grid-column: 2 / 4;
}

.product-option__body {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin-left: 0;
  align-self: flex-start;
  text-align: left;

  .show-price {
    grid-column: 2 / 4;
  }

  * {
    margin-top: 0;
  }

  :first-child {
    margin-top: 0;
  }

  &:last-child,
  :last-child {
    margin-bottom: 0;
  }

  ::v-deep ul,
  ::v-deep ol {
    padding: 0 0 0 19px;
    margin: 0;
  }
}

.product-option__price {
  margin: 0;

  &-old {
    color: #afafaf;
    text-decoration: line-through;
    font-size: $small-font-size;
    font-weight: $font-bold;
  }

  &-current {
    font-size: 18px;
    font-weight: 700;
    line-height: 18px;
  }

  &-included {
    color: $brand-orange;
  }

  .pppw {
    font-size: 12px;
    font-weight: 400;
    line-height: 100%;
  }

  @include media("<large") {
    text-align: left;
  }
}

.product-option__ribbon {
  position: absolute;
  top: 12px;
  right: 12px;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: $white;
  background-color: $grey-dark;
  padding: 4px 8px;
  border-radius: 80px;
}

.image-spacer {
  min-width: 30px;
  grid-row: 1 / 2;

  .show-price > & {
    grid-row: 1 / 3;
  }
}

.modal-footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 1rem;

  @include media("<large") {
    flex-direction: column-reverse;
    align-items: stretch;
  }
}

.right-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @include media("<large") {
    flex-direction: row;
    justify-content: center;
    width: 100%;
    order: 3;
  }
}

.back-btn-modal {
  overflow: hidden;
  background: none;
  border: none;
  color: #3b4143;
  text-overflow: ellipsis;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;

  @include media("<large") {
    order: 2;

    &.btn-mobile-hide {
      display: none;
    }
  }

  @include media(">large") {
    &.btn-desktop-hide {
      display: none;
    }
  }
}

.btn-update-broadband {
  display: flex;
  width: 250px;
  height: 60px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  background: $brand-orange;
  color: #fff;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  border: none;
  cursor: pointer;

  &:disabled {
    background: $grey-mid;
    color: $grey-dark;
    cursor: not-allowed;
  }

  @include media("<large") {
    width: 299px;
    height: 50px;
    padding: 8px 16px;
    justify-content: center;
    align-items: center;
    gap: 10px;
  }
}

.update-total {
  flex: 1;
  color: #3b4143;
  text-align: right;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;

  @include media("<large") {
    order: 1;
    text-align: center;
    margin: 0 0 10px 0;
  }
}

.bottom-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  flex-direction: row-reverse;

  @include media(">=large") {
    width: auto;
    gap: 1rem;
    justify-content: flex-end;
  }

  @include media(">=large") {
    flex-direction: row-reverse;
  }
}

.checkbox-option {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 16px 0;
  gap: 16px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #3b4143;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  flex-direction: row;

  @include media("<medium") {
    justify-content: space-between;
    flex-direction: row-reverse;
    gap: 12px;
    width: 100%;
  }
}

.info-toggle-checkbox {
  font-weight: normal;

  &.info-desktop-hide {
    @include media(">large") {
      display: none;
    }
  }

  &.info-mobile-hide {
    @include media("<large") {
      display: none;
    }
  }
}

.no-broadband-checkbox {
  align-items: flex-start;
  gap: 10px;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #6b7280;
  background: #fff;
  position: relative;

  @include media("<large") {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex-shrink: 0;
  }
}

.form-checkbox {
  border-radius: 6px;
  appearance: none;
  -webkit-appearance: none;
  background-color: white;
  border: 1px solid #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @include media("<large") {
    width: 24px;
    height: 24px;
  }
}

.form-checkbox:checked {
  background-color: $brand-blue;
  border-color: $brand-blue;
}

.form-checkbox:checked::after {
  content: url("~@/assets/svg/check.svg?external");
}
</style>
